import { Component, ErrorInfo, ReactNode } from 'react';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export default class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('App crashed:', error, errorInfo);
    this.setState({ errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-nexa-dark flex items-center justify-center px-4">
          <div className="max-w-2xl w-full bg-white/5 border border-red-400/30 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-4">
              <i className="ri-error-warning-line text-3xl text-red-400"></i>
              <h1 className="text-2xl font-bold text-white">Something went wrong</h1>
            </div>
            <p className="text-sky-200/80 mb-6">
              The portal hit an unexpected error. The details below help identify the cause —
              you can copy them or send a screenshot to support.
            </p>
            <pre className="bg-black/40 text-red-300 text-xs rounded-lg p-4 overflow-auto max-h-64 whitespace-pre-wrap">
              {this.state.error?.toString()}
              {'\n\n'}
              {this.state.errorInfo?.componentStack}
            </pre>
            <div className="mt-6 flex gap-3">
              <button
                onClick={() => window.location.reload()}
                className="bg-yellow-500 hover:bg-yellow-600 text-blue-900 font-semibold px-6 py-3 rounded-lg transition-colors cursor-pointer"
              >
                Reload Portal
              </button>
              <button
                onClick={() => window.location.assign('/')}
                className="border border-sky-400 text-blue-200 hover:text-white px-6 py-3 rounded-lg font-semibold transition-colors cursor-pointer"
              >
                Back to Login
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
